import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Mail, Phone, MessageCircle, Clock, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { HelpChat } from "@/components/support/HelpChat";

const FAQ_AR = [
  ["كيف أفتح حسابًا؟", "اضغط ابدأ الآن، وأخبرنا باسمك وبريدك الإلكتروني وبلدك، ثم أجب عن ست أسئلة قصيرة عن هدفك. يستغرق التحقق من هويتك دقائق قليلة ويمكنك التصفّح أثناء ذلك."],
  ["كم أحتاج للبدء؟", "لا يوجد حد أدنى. يمكنك شراء جزء من السهم — 100 ريال قطري من سهم Apple بداية طبيعية تمامًا."],
  ["كيف أشتري سهمًا؟", "افتح أي شركة، واكتب المبلغ الذي تريد استثماره بعملتك، ثم اسحب للتأكيد. ونحن نحسب عدد الأسهم لك."],
  ["هل أموالي آمنة؟", "تُحفظ أموالك في حساب عملاء منفصل وتُسجَّل ممتلكاتك باسمك. الجلسات مشفّرة وتنتهي بعد 30 دقيقة من عدم النشاط."],
  ["ما هي التكاليف؟", "0.1% على كل صفقة، بدون رسوم حساب، وبدون حد أدنى، وبدون رسوم إيداع. ويُعرض سعر تحويل العملة قبل التأكيد."],
  ["هل الخدمة متوافقة مع الشريعة؟", "يُفرز كل سهم وفق معايير AAOIFI في النشاط والنسب المالية. شغّل فلتر الحلال لعرض الأسماء المتوافقة فقط، واستخدم صفحة الزكاة لحساب ما يجب عليك."],
  ["كيف أسحب أموالي؟", "المحفظة ← تحويل أموال. التحويلات المحلية تصل عادةً في نفس يوم العمل، ونعرض الرسوم والسعر قبل التأكيد."],
];

const FAQ_EN = [
  ["How do I open an account?",
   "Tap Get started, tell us your name, email and country, and answer six short questions about your goal. Verifying your identity takes a couple of minutes and you can browse while it finishes."],
  ["How much do I need to start?",
   "There is no minimum. You can buy a fraction of a share — 100 QAR of Apple is a perfectly normal first order."],
  ["How do I buy a stock?",
   "Open any company, type the amount you want to spend in your own currency, and swipe to confirm. We work out the number of shares for you."],
  ["Is my money safe?",
   "Your cash sits in a segregated client account and your holdings are registered in your name. Sessions are encrypted and time out after 30 minutes of inactivity."],
  ["What does it cost?",
   "0.1% on each trade, no account fee, no minimum, no charge to deposit. Currency conversion is shown before you confirm."],
  ["Is it Shariah-compliant?",
   "Every listing is screened against AAOIFI rules on business activity and financial ratios. Turn on the Halal filter to see only compliant names, and use Zakat to work out what you owe."],
  ["How do I get my money out?",
   "Wallet → Send money. Local transfers usually land the same working day; we show the fee and the rate before you confirm."],
];

export default function Help() {
  const navigate = useNavigate();
  const { t, lang } = useApp();
  const FAQ = lang === "ar" ? FAQ_AR : FAQ_EN;
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "account", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.get("/support/topics").then(({ data }) => setMeta(data))
      .catch((e) => console.error("Couldn't load support topics:", e));
  }, []);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/support/contact", form);
      setSent(data);
      toast.success("Message sent", { description: data.message });
    } catch (e) {
      toast.error("Couldn't send that", { description: e?.response?.data?.detail?.[0]?.msg
        || e?.response?.data?.detail || "Please check your details and try again." });
    } finally { setBusy(false); }
  };

  const ready = form.name.trim().length > 1 && /.+@.+\..+/.test(form.email) && form.message.trim().length > 9;

  return (
    <div className="min-h-screen bg-[#F5F3FA] text-[#1A1330]" data-testid="help-page">
      <PublicHeader />
      <header className="border-b border-black/5 bg-white/70 backdrop-blur">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="help-back">
            <ArrowLeft className="h-4 w-4 me-1.5" />{t("cj_back")}
          </Button>
          <span className="text-lg font-semibold">{t("cj_help")}</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{t("hlp_title")}</h1>
        <p className="mt-2 text-[#1A1330]/70 max-w-xl">
          {t("hlp_sub")}
        </p>

        <div className="grid gap-3 sm:grid-cols-3 mt-7">
          {[
            { icon: Mail, label: t("hlp_email_us"), value: meta?.email || "team@tharwy.com",
              href: `mailto:${meta?.email || "team@tharwy.com"}`, testid: "help-email" },
            { icon: MessageCircle, label: t("hlp_whatsapp"), value: meta?.whatsapp || "+974 4000 1234",
              href: `https://wa.me/${(meta?.whatsapp || "").replace(/[^0-9]/g, "")}`, testid: "help-whatsapp" },
            { icon: Phone, label: t("hlp_call_us"), value: meta?.phone || "+974 4000 1234",
              href: `tel:${(meta?.phone || "").replace(/\s/g, "")}`, testid: "help-phone" },
          ].map((c) => (
            <a key={c.label} href={c.href} target="_blank" rel="noreferrer" data-testid={c.testid}
              className="rounded-2xl border border-black/5 bg-white p-4 hover:shadow-md transition-shadow">
              <c.icon className="h-5 w-5 text-[#3E2E65]" />
              <div className="mt-2.5 text-sm font-semibold">{c.label}</div>
              <div className="text-sm text-[#1A1330]/60 numeric">{c.value}</div>
            </a>
          ))}
        </div>

        <p className="mt-3 flex items-center gap-2 text-xs text-[#1A1330]/60">
          <Clock className="h-3.5 w-3.5" />{t("hlp_hours")}
        </p>

        <div className="grid gap-6 lg:grid-cols-5 mt-9">
          <Card className="p-5 sm:p-6 lg:col-span-3 border-black/5" data-testid="help-contact-card">
            <h2 className="text-lg font-semibold">{t("hlp_send_message")}</h2>
            {sent ? (
              <div className="mt-5 rounded-xl bg-emerald-50 p-4" data-testid="help-sent">
                <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                <p className="mt-2 text-sm text-emerald-900">{sent.message}</p>
                <Button variant="outline" className="mt-3" onClick={() => { setSent(null); setForm({ ...form, message: "" }); }}
                  data-testid="help-send-another">{t("hlp_send_another")}</Button>
              </div>
            ) : (
              <div className="mt-4 space-y-3.5">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>{t("hlp_your_name")}</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder={t("hlp_name_ph")} data-testid="help-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("email")}</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com" data-testid="help-email-input" />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>{t("hlp_phone_opt")}</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+974 …" data-testid="help-phone-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("hlp_about")}</Label>
                    <select value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}
                      data-testid="help-topic-select"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                      {(meta?.topics || [{ id: "account" }]).map((o) => (
                        <option key={o.id} value={o.id}>{t(`hlp_t_${o.id}`)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("hlp_your_message")}</Label>
                  <Textarea rows={5} value={form.message} placeholder={t("hlp_msg_ph")}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="help-message-input" />
                </div>
                <Button className="w-full bg-[#3E2E65] hover:bg-[#33265A] text-white"
                  disabled={busy || !ready} onClick={send} data-testid="help-send-button">
                  <Send className="h-4 w-4 me-1.5" />{busy ? t("hlp_sending") : t("hlp_send")}
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-5 sm:p-6 lg:col-span-2 border-black/5" data-testid="help-faq-card">
            <h2 className="text-lg font-semibold">{t("hlp_faq")}</h2>
            <Accordion type="single" collapsible className="mt-2">
              {FAQ.map(([q, a], i) => (
                <AccordionItem key={q} value={`q${i}`}>
                  <AccordionTrigger className="text-start text-sm" data-testid={`help-faq-${i}`}>
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#1A1330]/70 leading-relaxed">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Card>
        </div>
      </main>
      <HelpChat />
    </div>
  );
}
